import React, { useState } from 'react';
import { User, useAuth0 } from '@auth0/auth0-react';
import { useApiService, ApiResponse } from '../services/api';

interface UserProfileProps {
  user?: User;
}

export const UserProfile: React.FC<UserProfileProps> = ({ user }) => {
  const { callProtectedApi, callUserProfile } = useApiService();
  const [apiResult, setApiResult] = useState<ApiResponse | null>(null);
  const [userProfileResult, setUserProfileResult] = useState<any>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [userProfileError, setUserProfileError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  const handleCallApi = async () => {
    try {
      setIsLoading(true);
      setApiError(null);
      setApiResult(null);

      const response = await callProtectedApi('GET');
      setApiResult(response);
    } catch (error) {
      console.error('API call failed:', error);
      setApiError(error instanceof Error ? error.message : 'API call failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCallUserProfile = async () => {
    try {
      setIsProfileLoading(true);
      setUserProfileError(null);
      setUserProfileResult(null);

      const response = await callUserProfile();
      setUserProfileResult(response);
    } catch (error) {
      console.error('User profile API call failed:', error);
      setUserProfileError(
        error instanceof Error ? error.message : 'User profile API call failed'
      );
    } finally {
      setIsProfileLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '10px',
        padding: '20px',
        margin: '20px 0',
        textAlign: 'left',
        maxWidth: '400px',
      }}
    >
      <h2>Welcome!</h2>

      {user.picture && (
        <img
          src={user.picture}
          alt="Profile"
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            marginBottom: '15px',
          }}
        />
      )}

      <div style={{ marginBottom: '10px' }}>
        <strong>Email:</strong> {user.email}
      </div>

      {user.name && (
        <div style={{ marginBottom: '10px' }}>
          <strong>Name:</strong> {user.name}
        </div>
      )}

      <div style={{ marginBottom: '10px' }}>
        <strong>Email Verified:</strong>{' '}
        {user.email_verified ? '✅ Yes' : '❌ No'}
      </div>

      <div style={{ marginBottom: '10px' }}>
        <strong>Last Login:</strong>{' '}
        {user.updated_at ? new Date(user.updated_at).toLocaleString() : 'N/A'}
      </div>

      <div style={{ marginTop: '20px' }}>
        <button
          onClick={handleCallApi}
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            opacity: isLoading ? 0.6 : 1,
            marginBottom: '10px',
          }}
        >
          {isLoading ? 'Calling API...' : 'Test Protected API'}
        </button>

        <button
          onClick={handleCallUserProfile}
          disabled={isProfileLoading}
          style={{
            width: '100%',
            padding: '10px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: isProfileLoading ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            opacity: isProfileLoading ? 0.6 : 1,
          }}
        >
          {isProfileLoading ? 'Fetching Profile...' : 'Get App User Profile'}
        </button>

        {userProfileError && (
          <div
            style={{
              marginTop: '15px',
              padding: '10px',
              backgroundColor: 'rgba(220, 53, 69, 0.2)',
              border: '1px solid #dc3545',
              borderRadius: '5px',
              fontSize: '14px',
            }}
          >
            <strong style={{ color: '#dc3545' }}>
              User Profile API Error:
            </strong>
            <br />
            <span style={{ color: '#dc3545' }}>{userProfileError}</span>
          </div>
        )}

        {apiError && (
          <div
            style={{
              marginTop: '15px',
              padding: '10px',
              backgroundColor: 'rgba(220, 53, 69, 0.2)',
              border: '1px solid #dc3545',
              borderRadius: '5px',
              fontSize: '14px',
            }}
          >
            <strong style={{ color: '#dc3545' }}>API Error:</strong>
            <br />
            <span style={{ color: '#dc3545' }}>{apiError}</span>
          </div>
        )}

        {userProfileResult && (
          <div
            style={{
              marginTop: '15px',
              padding: '10px',
              backgroundColor: 'rgba(40, 167, 69, 0.2)',
              border: '1px solid #28a745',
              borderRadius: '5px',
              fontSize: '14px',
            }}
          >
            <strong style={{ color: '#28a745' }}>
              App User Profile Response:
            </strong>
            <details style={{ marginTop: '10px' }}>
              <summary style={{ cursor: 'pointer' }}>
                View User Profile Data
              </summary>
              <pre
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  padding: '10px',
                  borderRadius: '5px',
                  fontSize: '12px',
                  overflow: 'auto',
                  marginTop: '5px',
                }}
              >
                {JSON.stringify(userProfileResult, null, 2)}
              </pre>
            </details>
          </div>
        )}

        {apiResult && (
          <div
            style={{
              marginTop: '15px',
              padding: '10px',
              backgroundColor: 'rgba(40, 167, 69, 0.2)',
              border: '1px solid #28a745',
              borderRadius: '5px',
              fontSize: '14px',
            }}
          >
            <strong style={{ color: '#28a745' }}>API Response:</strong>
            <div style={{ marginTop: '10px' }}>
              <p>
                <strong>Message:</strong> {apiResult.message}
              </p>
              <p>
                <strong>User ID:</strong> {apiResult.user.id}
              </p>
              <p>
                <strong>Scopes:</strong> {apiResult.user.scope.join(', ')}
              </p>
              <details style={{ marginTop: '10px' }}>
                <summary style={{ cursor: 'pointer' }}>
                  View Raw Response
                </summary>
                <pre
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    padding: '10px',
                    borderRadius: '5px',
                    fontSize: '12px',
                    overflow: 'auto',
                    marginTop: '5px',
                  }}
                >
                  {JSON.stringify(apiResult, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        )}
      </div>

      <details style={{ marginTop: '15px' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
          View Full Profile Data
        </summary>
        <pre
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            padding: '10px',
            borderRadius: '5px',
            fontSize: '12px',
            overflow: 'auto',
            marginTop: '10px',
            whiteSpace: 'pre-wrap',
          }}
        >
          {JSON.stringify(user, null, 2)}
        </pre>
      </details>
    </div>
  );
};
